import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FiSearch } from "react-icons/fi";
import { Link } from "react-router-dom";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast } from "react-toastify";
import { setHitID } from "../../config/HitIDStorage";
import axios from "axios";
import apiKey from "../../config/apiKey";
import { FaRegTrashAlt } from "react-icons/fa";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

/* ---------- Small UI helpers ---------- */
const Badge = ({ tone = "neutral", children, onClick, title }) => {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 ring-slate-200",
    success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    danger: "bg-rose-50 text-rose-700 ring-rose-200",
    info: "bg-blue-50 text-blue-700 ring-blue-200",
  };
  const base =
    "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ring-1 focus:outline-none focus:ring-2 focus:ring-offset-2";
  const Comp = onClick ? "button" : "span";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      className={`${base} ${tones[tone]}`}
      onClick={onClick}
      title={title}
    >
      {children}
    </Comp>
  );
};

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {[...Array(7)].map((_, i) => (
      <td key={i} className="py-4 px-2">
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </td>
    ))}
  </tr>
);

export default function SrudentTable({ heading, SubHeading }) {
  // id Set
  const handleHitIDReceived = (id) => setHitID(id);

  // Data fetching / state
  const admin = JSON.parse(localStorage.getItem("edo-admin"));
  const [data, setData] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("Newest");
  const [expandedRow, setExpandedRow] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Soft Delete modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [softDeleteID, setSoftDeleteID] = useState(null);

  /* ---------- Debounce search ---------- */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /* ---------- Fetch ---------- */
  const fetchData = useCallback(() => {
    setErr("");
    setLoading(true);
    axios
      .get(`${apiKey}/api/edolutions/admin/users/role/3`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      })
      .then((response) => {
        const fetchedData = (response.data || []).map((item) => ({
          id: item.id,
          column1: item.name,
          column2: item.email,
          column3: item.phone,
          column4: item.status, // "Active" | "Inactive"
          column6: item.institute,
          column7: item.city,
          column8: item.country,
          column9: item.address,
          column10: item.class_name,
          path: ".././mentee-dash",
        }));
        setData(fetchedData);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setErr("Couldn’t load Mentees. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [admin?.token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---------- Actions ---------- */
  const updateStatus = async (id) => {
    try {
      const response = await fetch(
        `${apiKey}/api/edolutions/admin/updatestatusstudent/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${admin?.token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unknown error");

      toast.success(data.message, {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(`Error updating status: ${error.message}`, {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
    }
  };

  const handleDeleteClick = (id) => {
    setSoftDeleteID(id);
    setIsModalOpen(true);
  };

  const softDelete = async (id) => {
    try {
      const response = await fetch(
        `${apiKey}/api/edolutions/admin/softdeletestudent/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${admin?.token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unknown error");

      toast.success(data.message, {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });
      fetchData();
    } catch (error) {
      console.error("Error updating visibility:", error);
      toast.error(`Error updating visibility: ${error.message}`, {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
    }
  };

  /* ---------- Derived data (memoized) ---------- */
  const filteredData = useMemo(() => {
    if (!debouncedSearch) return data;
    const term = debouncedSearch.toLowerCase();
    return data.filter((item) => {
      const n = (item.column1 || "").toLowerCase();
      const e = (item.column2 || "").toLowerCase();
      const p = (item.column3 || "").toLowerCase();
      const cls = (item.column10 || "").toLowerCase();
      return n.includes(term) || e.includes(term) || p.includes(term) || cls.includes(term);
    });
  }, [data, debouncedSearch]);

  const sortedData = useMemo(() => {
    const copy = [...filteredData];
    copy.sort((a, b) => (sortOrder === "Newest" ? b.id - a.id : a.id - b.id));
    return copy;
  }, [filteredData, sortOrder]);

  // pagination
  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = Math.min((currentPage - 1) * itemsPerPage, totalItems);
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentData = useMemo(
    () => sortedData.slice(startIndex, endIndex),
    [sortedData, startIndex, endIndex]
  );

  useEffect(() => {
    // Reset page on these changes
    setCurrentPage(1);
  }, [itemsPerPage, sortOrder, debouncedSearch]);

  // windowed page numbers (max 5)
  const pageRange = useMemo(() => {
    const maxBtns = 5;
    if (totalPages <= maxBtns) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - (maxBtns - 1)));
    return Array.from({ length: maxBtns }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const handleToggleDetails = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  /* ---------- Render ---------- */
  return (
    <div className="relative my-8 flex flex-col w-full shadow-sm bg-white rounded-2xl border border-gray-200">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5 border-b border-gray-100">
        <div>
          <p className="text-xl font-bold text-gray-900">{heading}</p>
          {SubHeading && <p className="text-sm text-[#4FD1C5] mt-1">{SubHeading}</p>}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative border rounded-md flex items-center px-3 py-2 shadow-sm">
            <FiSearch className="h-5 w-5 absolute left-2 text-gray-500" />
            <input
              type="text"
              className="outline-none pl-7 text-sm"
              placeholder="Search name, email, phone, class…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="relative">
            <select
              className="border shadow-sm rounded-md px-3 py-2 outline-none text-sm"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="Newest">Sorted by: Newest</option>
              <option value="Oldest">Sorted by: Oldest</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {err && (
        <div className="mx-6 mt-4 mb-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="mt-4 mx-6 mb-6 overflow-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-50 text-[#6b7280] sticky top-0 z-10">
            <tr>
              <th className="py-3 px-2 font-medium">Mentee</th>
              <th className="py-3 px-2 font-medium">Email</th>
              <th className="py-3 px-2 font-medium">Phone</th>
              <th className="py-3 px-2 font-medium">Class</th>
              <th className="py-3 px-2 font-medium">Details</th>
              <th className="py-3 px-2 font-medium">Status</th>
              <th className="py-3 px-2 font-medium text-center">Delete</th>
            </tr>
          </thead>

        <tbody className="divide-y">
          {loading && (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          )}

          {!loading && totalItems === 0 && (
            <tr>
              <td colSpan={7} className="py-8 px-2 text-center text-gray-500">
                No data available
              </td>
            </tr>
          )}

          {!loading &&
            currentData.map((item, idx) => {
              const globalIndex = startIndex + idx;
              const isExpanded = expandedRow === globalIndex;
              const statusTone = item.column4 === "Active" ? "success" : "danger";

              return (
                <React.Fragment key={item.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="py-4 px-2">
                      <Link
                        to={item.path}
                        onClick={() => handleHitIDReceived(item.id)}
                        className="capitalize font-medium text-gray-900 hover:underline"
                      >
                        {item.column1 || "-"}
                      </Link>
                    </td>
                    <td className="py-4 px-2">{item.column2 || "-"}</td>
                    <td className="py-4 px-2">{item.column3 || "-"}</td>
                    <td className="py-4 px-2">{item.column10 || "-"}</td>
                    <td className="py-4 px-2">
                      <button
                        className="text-[#EE5F2B] hover:opacity-80"
                        onClick={() => handleToggleDetails(globalIndex)}
                        title={isExpanded ? "Hide details" : "Show details"}
                      >
                        {isExpanded ? (
                          <AiOutlineEyeInvisible className="text-lg" />
                        ) : (
                          <AiOutlineEye className="text-lg" />
                        )}
                      </button>
                    </td>
                    <td className="py-4 px-2">
                      <Badge
                        tone={statusTone}
                        onClick={() => updateStatus(item.id)}
                        title="Toggle status"
                      >
                        {item.column4}
                      </Badge>
                    </td>
                    <td className="py-4 px-2 text-center">
                      <button
                        className="text-rose-600 hover:text-rose-700"
                        onClick={() => handleDeleteClick(item.id)}
                        title="Remove"
                      >
                        <FaRegTrashAlt className="text-lg" />
                      </button>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="bg-white">
                        <div className="m-2 rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] text-white">
                              <tr>
                                <th className="py-3 px-2 text-left">Institute</th>
                                <th className="py-3 px-2 text-left">City</th>
                                <th className="py-3 px-2 text-left">Country</th>
                                <th className="py-3 px-2 text-left">Address</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="py-4 px-2">{item.column6 || "-"}</td>
                                <td className="py-4 px-2">{item.column7 || "-"}</td>
                                <td className="py-4 px-2">{item.column8 || "-"}</td>
                                <td className="py-4 px-2">{item.column9 || "-"}</td>
                              </tr>
                            </tbody>
                          </table>
                          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 p-3 text-xs text-gray-500">
                            {/* {item.column6 && <Badge tone="info">Has Institute</Badge>} */}
                            {item.column7 && <Badge tone="neutral">City: {item.column7}</Badge>}
                            {item.column10 && <Badge tone="success">Class: {item.column10}</Badge>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap gap-5 justify-between items-center w-full text-sm px-6 pb-6">
        <div className="flex items-center gap-2 text-gray-600">
          <select
            className="text-sm border rounded-lg px-2 py-1 bg-white"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
          >
            {[10, 20, 30, 40, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>Showing</span>
          <span className="font-medium text-gray-800">
            {totalItems === 0 ? 0 : startIndex + 1}-{endIndex}
          </span>
          <span>of {totalItems} entries</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <MdKeyboardArrowLeft />
          </button>

          {pageRange.map((p) => (
            <button
              key={p}
              className={`text-base rounded-lg px-3 py-1 ${
                p === currentPage ? "text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" : "text-black bg-gray-100"
              }`}
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}

          <span>of {totalPages} pages</span>

          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <MdKeyboardArrowRight />
          </button>
        </div>
      </div>

      {/* Confirm Delete Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="px-5 py-4 border-b">
              <h3 className="text-lg font-semibold">Remove Mentee</h3>
            </div>
            <div className="px-5 py-5 text-sm text-gray-700">
              This action will permanently remove the Mentee's data.
              <br />
              Are you sure you want to proceed?
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  softDelete(softDeleteID);
                  setIsModalOpen(false);
                }}
                className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg text-sm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
